import { useState, useEffect } from "react";
import { useComponentTranslation } from "@/services/i18n";
import { ResponsiveLine } from "@nivo/line";
import { ResponsivePie } from "@nivo/pie";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";

import api from "@/services/api";
import useStore from "@/services/store";
import Loader from "@/components/loader";

// CircularMatchingScore component (inlined)
function CircularMatchingScore({ score = 0 }) {
  if (score === null)
    return (
      <div className="shrink-0 flex items-center justify-center overflow-hidden relative" style={{ height: "35px", width: "35px", maxHeight: "35px", maxWidth: "35px" }}></div>
    );
  return (
    <div
      className="shrink-0 flex items-center justify-center overflow-hidden relative border border-gray-200 rounded-full"
      style={{ height: "35px", width: "35px", maxHeight: "35px", maxWidth: "35px" }}
    >
      <svg className="w-full h-full" viewBox="0 0 60 60">
        <circle
          cx="30"
          cy="30"
          r="25"
          fill="none"
          stroke={score > 70 ? "#10B981" : score > 50 ? "#3B82F6" : "#6B7280"}
          strokeWidth="5"
          strokeDasharray={`${score !== null ? (2 * Math.PI * 25 * score) / 100 : 0} ${2 * Math.PI * 25 - (score !== null ? (2 * Math.PI * 25 * score) / 100 : 0)}`}
          transform="rotate(-90 30 30)"
        />
      </svg>
      <span
        className="text-sm font-semibold leading-none absolute"
        style={{
          color: score > 70 ? "#10B981" : score > 50 ? "#3B82F6" : "#6B7280",
          fontSize: "0.60rem",
        }}
      >
        {score === null ? "N/A" : `${score}%`}
      </span>
    </div>
  );
}

const translations = {
  en: {
    title: "Statistics",
    cards: {
      candidates: "Total candidates",
      selected_candidates: "Selected candidates",
      recruited_candidates: "Recruited candidates",
    },
    charts: {
      applications_title: "Applications over time",
      applications_by_source: "Applications by source",
      matching_scores: "Matching scores",
      date: "Date",
      applications: "Applications",
      source: "Source",
    },
  },
  fr: {
    title: "Statistiques",
    cards: {
      candidates: "Total des candidats",
      selected_candidates: "Candidats sélectionnés",
      recruited_candidates: "Candidats recrutés",
    },
    charts: {
      applications_title: "Candidatures dans le temps",
      applications_by_source: "Candidatures par source",
      matching_scores: "Scores de correspondance",
      date: "Date",
      applications: "Candidatures",
      source: "Source",
    },
  },
};

export default function Stats() {
  const { t } = useComponentTranslation(translations);
  const { organization } = useStore();
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState("last_month");
  const [selectedTags, setSelectedTags] = useState("all_tags");
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ fromDate: new Date().toISOString().split("T")[0], toDate: new Date().toISOString().split("T")[0] });

  useEffect(() => {
    fetchStatistics();
  }, [selectedPeriod, selectedTags, dateRange, id]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await api.post(`/statistics/search`, {
        job_id: id,
        organization_id: organization?._id, organization_name: organization?.name,
        onlyMyJobAds: true,
      });
      setStats(response.data);
    } catch (e) {
      console.error("Error fetching job ad statistics:", e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  const hasCandidates = stats.candidates?.total > 0;

  return (
    <div className="p-6 bg-white">
      {/* Back Button */}
      <button
        onClick={() => navigate(`/recruiter/job-ads/${id}`)}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
      >
        <FiArrowLeft className="w-5 h-5" />
        <span className="font-medium">Back</span>
      </button>

      {/* Header */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-900">{t("title")}</h2>
      </div>

      {/* Stats Cards */}
      <StatsCards candidates={stats.candidates} />

      {/* Charts Section */}
      {hasCandidates && (
        <div className="flex flex-col gap-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ApplicationsChart stats={stats} />
            <ApplicationsBySource stats={stats.applications?.bySource || []} />
          </div>
          <MatchingScores stats={stats.matching || []} />
        </div>
      )}
    </div>
  );
}

const StatsCards = ({ candidates }) => {
  const { t } = useComponentTranslation(translations);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
        <div className="text-4xl font-bold text-primary mb-2">{candidates?.total || 0}</div>
        <div className="text-sm text-gray-600 font-medium">{t("cards.candidates")}</div>
      </div>
      <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
        <div className="text-4xl font-bold text-green-600 mb-2">{candidates?.selected || 0}</div>
        <div className="text-sm text-gray-600 font-medium">{t("cards.selected_candidates")}</div>
      </div>
      <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
        <div className="text-4xl font-bold text-primary mb-2">{candidates?.recruited || 0}</div>
        <div className="text-sm text-gray-600 font-medium">{t("cards.recruited_candidates")}</div>
      </div>
    </div>
  );
};

const ApplicationsChart = ({ stats }) => {
  const { t } = useComponentTranslation(translations);
  const lineChartData = [
    {
      id: "applications",
      data: stats.applications?.overTime || [],
    },
  ];

  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">{t("charts.applications_title")}</h3>
      <div style={{ height: "400px" }}>
        <ResponsiveLine
          data={lineChartData}
          margin={{ top: 20, right: 20, bottom: 50, left: 50 }}
          xScale={{ type: "point" }}
          yScale={{ type: "linear", min: 0, max: "auto" }}
          curve="monotoneX"
          axisBottom={{
            tickRotation: -45,
            tickSize: 5,
            tickPadding: 5,
            legend: t("charts.date"),
            legendOffset: 40,
            legendPosition: "middle",
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: t("charts.applications"),
            legendOffset: -40,
            legendPosition: "middle",
          }}
          pointSize={8}
          pointColor="white"
          pointBorderWidth={2}
          pointBorderColor={{ from: "serieColor" }}
          enableArea={true}
          areaOpacity={0.1}
          useMesh={true}
          enableSlices="x"
          colors={["#6C4CFF"]}
          sliceTooltip={({ slice }) => {
            return (
              <div className="bg-white p-2 border border-gray-200 rounded shadow">
                <div className="text-sm font-medium">{slice.points[0].data.x}</div>
                <div className="text-sm text-gray-600">
                  {t("charts.applications")}: {slice.points[0].data.y}
                </div>
              </div>
            );
          }}
        />
      </div>
    </div>
  );
};

const ApplicationsBySource = ({ stats }) => {
  const { t } = useComponentTranslation(translations);
  const pieChartData = stats.map((source) => ({
    id: source.id,
    value: source.percentage,
    label: source.id,
  }));

  const getProgressBarColor = (sourceName) => {
    switch (sourceName) {
      case "Jobfirst":
        return "bg-primary";
      case "Indeed":
        return "bg-pink-500";
      case "Linkedin":
        return "bg-yellow-400";
      case "Figaroemploi":
        return "bg-green-500";
      default:
        return "bg-gray-300";
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">{t("charts.applications_by_source")}</h3>
      <div className="w-full mb-8">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 text-sm text-gray-500">
              <th className="text-left p-3 font-medium rounded-l-md">{t("charts.source").toUpperCase()}</th>
              <th className="text-left p-3 font-medium">{t("charts.applications").toUpperCase()}</th>
              <th className="p-3 font-medium rounded-r-md"></th>
            </tr>
          </thead>
          <tbody>
            {stats.map((source, index) => (
              <tr key={source.id} className={`${index !== stats.length - 1 ? "border-b border-gray-200" : ""}`}>
                <td className="py-3 pl-3 text-gray-900">{source.id}</td>
                <td className="py-3 text-left pr-3 text-gray-900">{source.value}</td>
                <td className="py-3 pl-4 pr-3">
                  <div className="flex items-center">
                    <span className="ml-2 text-sm text-gray-500 w-16">{source.percentage.toFixed(2)}%</span>
                    <div className="grow bg-gray-100 rounded-full h-2">
                      <div className={`${getProgressBarColor(source.id)} h-2 rounded-full`} style={{ width: `${source.percentage}%` }} />
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border-t border-gray-200 pt-6">
        <div className="h-[300px]">
          <ResponsivePie
            data={pieChartData}
            margin={{ top: 40, right: 80, bottom: 40, left: 80 }}
            innerRadius={0.5}
            padAngle={0.7}
            cornerRadius={3}
            colors={({ id }) => {
              switch (id) {
                case "Jobfirst":
                  return "#6C4CFF";
                case "Indeed":
                  return "#EC4899";
                case "Linkedin":
                  return "#FBBF24";
                case "Figaroemploi":
                  return "#22C55E";
                default:
                  return "#93C5FD";
              }
            }}
            borderWidth={1}
            borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
            enableArcLinkLabels={false}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor="#4B5563"
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: "color" }}
            arcLabelsSkipAngle={10}
            arcLabelsTextColor="white"
            enableArcLabels={true}
            arcLabel={(d) => `${d.value}%`}
            legends={[
              {
                anchor: "right",
                direction: "column",
                justify: false,
                translateX: 60,
                translateY: 0,
                itemWidth: 80,
                itemHeight: 20,
                itemsSpacing: 4,
                symbolSize: 12,
                itemTextColor: "#4B5563",
                symbolShape: "circle",
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

const MatchingScores = ({ stats }) => {
  const { t } = useComponentTranslation(translations);
  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">{t("charts.matching_scores")}</h3>
      <div className="space-y-4">
        {stats.map((item, index) => (
          <div key={item.source} className={`flex justify-between items-center py-2 ${index !== stats.length - 1 ? "border-b border-gray-200" : ""}`}>
            <span className="text-gray-900">{item.source}</span>
            <CircularMatchingScore score={item.score} />
          </div>
        ))}
      </div>
    </div>
  );
};
